import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Dialog, IconButton, Typography, CircularProgress, Backdrop } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { styled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { getWithQueryApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { DateFilter } from "./DateFilter";
import { errorToast } from "../../../utils/helper";
import { usePermissions } from "../../../hooks/usePermissions";


const Search = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  minWidth: 0,
  height: "34px",
  padding: "0 2px 0 6px",
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box",
  overflow: "hidden",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(0, 1),
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

const StyledCancelIcon = styled(CancelIcon)(({ theme }) => ({
    marginRight: 16
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: "inherit",
    flex: "1 1 auto",
    width: "auto",
    minWidth: 0,
    "& .MuiInputBase-input": {
      width: "100%",
      minWidth: 0,
      padding: theme.spacing(0.5, 0.5, 0.5, 0),
      textOverflow: "ellipsis",
    },
    height: "30px",
  }));

const AdvancedSearchDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    maxWidth: "95vw",
    maxHeight: "95vh",
    width: "auto",
    height: "auto",
    margin: "auto",
    borderRadius: "8px",
    overflow: "auto",
  },
});

export const PowerSearch = ({ currentCompany, tenantString }) => {
    const { hasPermission, isGlobalSuperAdmin } = usePermissions(currentCompany);
    const canSearch = isGlobalSuperAdmin || hasPermission("Search: Access");

    const navigate = useNavigate();
    const location = useLocation();

    const cookies = new Cookies();
    const defaultcompany = cookies.get("defaultcompany");
    const selectedCompany = currentCompany || defaultcompany;

    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const previousCompanyRef = useRef(currentCompany);
    const searchValueRef = useRef(searchValue);
    searchValueRef.current = searchValue;

    useEffect(() => {
        if (String(previousCompanyRef.current ?? "") === String(currentCompany ?? "")) return;
        previousCompanyRef.current = currentCompany;
        const onSearchResults = location.pathname === "/searchResult";
        if (!onSearchResults && !String(searchValueRef.current || "").trim()) return;
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        input.select();
    }, [currentCompany, location.pathname]);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sId = params.get("searchId");
        if (sId) {
            try {
                const storedStr = localStorage.getItem(`search_${sId}`);
                if (storedStr) {
                    const stored = JSON.parse(storedStr);
                    if (stored && stored.searchValue) {
                        setSearchValue(stored.searchValue);
                    }
                }
            } catch (e) {
                console.error(e);
            }
        } else if (location.state && location.state.searchValue) {
            setSearchValue(location.state.searchValue);
        } else if (location.pathname !== "/searchResult") {
            setSearchValue("");
        }
    }, [location]);

    const handleAdvancedSearchOpen = () => {
        setAdvancedSearchOpen(true);
    };

    const handleAdvancedSearchClose = () => {
        setAdvancedSearchOpen(false);
    };

    const onSearchChange = (e) => {
        if (e.charCode === 13 && searchValue.trim().length >= 1) {
            getQuickSearch();
        }
    };

    const clearSearch = () => {
        setSearchValue("");
    };

    const getQuickSearch = (categoryID = null, startDate = null, endDate = null) => {
        setLoading(true);
        let queryParams = `selectedCompany=${selectedCompany}&searchQuery=${searchValue}${tenantString || ""}&pageNumber=1&pageSize=20`;
        
        if (categoryID) {
            queryParams += `&categoryID=${categoryID}`;
        }
        
        if (startDate) {
            queryParams += `&startDate=${startDate}`;
        }
        
        if (endDate) {
            queryParams += `&endDate=${endDate}`;
        }
    
        getWithQueryApiServices(apiRoutes.QUICK_SEARCH, queryParams)
            .then(({ data }) => {
                setLoading(false);
                navigate(ROUTE_PATHS.SEARCH_RESULT, { 
                    state: { 
                        data: data?.results || [], 
                        totalCount: data?.totalCount || 0,
                        searchValue,
                        queryParams,
                        searchType: "quick"
                    } 
                });
            })
            .catch((error) => { 
                setLoading(false);
                console.error("Search error:", error);
                errorToast(error.response?.data?.message || "Search failed. You might not have permission for this company.");
            });
    };

    if (!canSearch) return null;

    return (
        <>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: 'blur(3px)', flexDirection: 'column' }}
                open={loading}
            >
                <CircularProgress color="inherit" size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Searching...</Typography>
            </Backdrop>
            <Box sx={{ width: '100%', position: 'relative' }}>
                <Search
                    style={{
                        borderRadius: 4,
                        borderColor: (searchValue.length > 0 && searchValue.length < 1) ? "#d32f2f" : "#bdbdbd",
                        borderWidth: 1,
                        borderStyle: "solid",
                    }}
                >
                    <StyledInputBase
                        placeholder="Search"
                        inputProps={{ "aria-label": "search" }}
                        inputRef={inputRef}
                        name="searchValue"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyPress={(e) => onSearchChange(e)}
                        autoComplete="off"
                    />
                    {searchValue && (
                        <IconButton
                            aria-label="clear search"
                            size="small"
                            onClick={clearSearch}
                            sx={{ flexShrink: 0, p: 0.5 }}
                        >
                            <CancelIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        aria-label="advanced search"
                        size="small"
                        onClick={handleAdvancedSearchOpen}
                        sx={{ flexShrink: 0, p: 0.5 }}
                    >
                        <TuneIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="submit search"
                        size="small"
                        onClick={() => {
                            if (searchValue.trim().length >= 1) {
                                getQuickSearch();
                            }
                        }}
                        disabled={searchValue.trim().length < 1}
                        sx={{
                            flexShrink: 0,
                            p: 0.5,
                            color: "#7285b3",
                            "&.Mui-disabled": {
                                color: "#bdbdbd",
                            }
                        }}
                    >
                        <SearchIcon fontSize="small" />
                    </IconButton>
                </Search>
                {searchValue.length > 0 && searchValue.length < 1 && (
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: "#d32f2f", 
                            position: 'absolute', 
                            bottom: -18, 
                            left: 4,
                            fontSize: '10px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Minimum 1 character required
                    </Typography>
                )}
            </Box>

            {/* ✅ DIALOG FOR ADVANCED SEARCH - CENTERED */}
            <AdvancedSearchDialog
                open={advancedSearchOpen}
                onClose={handleAdvancedSearchClose}
                maxWidth={false}
            >
                <DateFilter 
                    searchValue={searchValue} 
                    setSearchValue={setSearchValue} 
                    setOpen={setAdvancedSearchOpen}
                    currentCompany={currentCompany}
                    tenantString={tenantString}
                />
            </AdvancedSearchDialog>
        </>
    );
};